/**
 * <sticky-add-to-cart>: shows the floating add to cart bar once the product's main Add to Cart
 * button has scrolled out of view above the screen, and hides it again when it comes back.
 *
 * - Follows the variant picker (`variant:change`) for the variant, availability and its label.
 *   The price follows by itself: it is a <product-price> inside the bar.
 * - Adds through window.MRLCart (assets/mrl-cart-bridge.js) so the cart drawer opens; falls back to
 *   the /cart/{id}:1 link when the bridge is missing.
 */
(() => {
  if (customElements.get('sticky-add-to-cart')) return;

  class StickyAddToCart extends HTMLElement {
    #abort = new AbortController();

    /** @type {IntersectionObserver | undefined} */
    #observer;

    connectedCallback() {
      const { signal } = this.#abort;
      const bar = this.querySelector('.sticky-atc');

      this.addEventListener('click', this.#handleClick, { signal });
      document.addEventListener('variant:change', this.#handleVariantChange, { signal });

      const target = document.querySelector(`buy-buttons[data-product-id="${this.dataset.productId}"]`);

      if (target && 'IntersectionObserver' in window) {
        // Show only when the main button is above the screen (scrolled past), not when it is still
        // below the fold on arrival.
        this.#observer = new IntersectionObserver(([entry]) => {
          this.#setVisible(Boolean(entry) && !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
        this.#observer.observe(target);
      } else if (bar) {
        // No main button on the page: show after a screen of scrolling
        window.addEventListener('scroll', () => this.#setVisible(window.scrollY > window.innerHeight * 0.8), {
          passive: true,
          signal,
        });
      }
    }

    disconnectedCallback() {
      this.#abort.abort();
      this.#observer?.disconnect();
    }

    /** @param {boolean} visible */
    #setVisible(visible) {
      const bar = this.querySelector('.sticky-atc');
      this.toggleAttribute('data-visible', visible);

      // Hidden means unreachable by keyboard and screen readers as well
      if (bar) {
        bar.toggleAttribute('inert', !visible);
        bar.setAttribute('aria-hidden', String(!visible));
      }
    }

    /** @param {Event} event */
    #handleVariantChange = (event) => {
      if (!(event instanceof CustomEvent) || !(event.target instanceof Element)) return;

      // Ignore pickers for other products on the page
      const picker = event.target.closest('variant-picker');
      if (picker instanceof HTMLElement && picker.dataset.productId !== this.dataset.productId) return;

      const { variant } = event.detail;
      this.dataset.variantId = String(variant.id);

      const button = this.querySelector('[data-add]');
      const label = this.querySelector('[data-label]');
      if (button instanceof HTMLButtonElement) button.disabled = !variant.available;
      if (label) label.textContent = (variant.available ? this.dataset.addText : this.dataset.soldOutText) ?? '';

      const title = this.querySelector('[data-variant-title]');
      if (title && Array.isArray(variant.options)) title.textContent = variant.options.join(' / ');
    };

    /** @param {MouseEvent} event */
    #handleClick = async (event) => {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('[data-add]');
      if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

      const id = Number(this.dataset.variantId);
      const cart = /** @type {any} */ (window).MRLCart;

      if (!cart) {
        window.location.href = `/cart/${id}:1`;
        return;
      }

      const status = this.querySelector('[data-status]');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      if (status) status.textContent = '';

      try {
        await cart.addItem(id, 1, { openDrawer: true });
      } catch (error) {
        if (status) status.textContent = error instanceof Error ? error.message : String(error);
      } finally {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    };
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
})();
